import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, EMPTY, Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthLoginResponseModel } from '../../auth/models/auth-login-response-model';
import { AuthFacade } from '../../auth/services/auth.facade';

@Injectable({
	providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {
	private readonly oAuthDisabled: boolean;

	// tslint:disable-next-line: no-any
	private refreshTokenSubject$: BehaviorSubject<any> = new BehaviorSubject<any>(null);
	private isRefreshing: boolean = false;

	constructor(
		private authFacade: AuthFacade,
		private router: Router
	) {
		this.oAuthDisabled = true;
	}

	intercept(request: HttpRequest<object>, next: HttpHandler): Observable<HttpEvent<object>> {
		request = this.attachHeaders(request);

		return next.handle(request).pipe(
			catchError((error: HttpErrorResponse) => {
				if (error instanceof HttpErrorResponse && error.status === 401) {
					if (!this.isRefreshing) {
						this.isRefreshing = true;
						this.refreshTokenSubject$.next(null);

						return this.authFacade.refreshToken$().pipe(
							switchMap((response: AuthLoginResponseModel) => {
								this.authFacade.setAccessToken(response.token);

								this.isRefreshing = false;
								this.refreshTokenSubject$.next(response.token);

								return next.handle(this.addAuthenticationToken(request));
							}),
							catchError(() => {
								this.isRefreshing = false;
								this.authFacade.logout();

								return EMPTY;
							})
						);
					} else {
						return this.refreshTokenSubject$.pipe(
							filter((accessToken) => accessToken !== null),
							take(1),
							switchMap(() => next.handle(this.addAuthenticationToken(request)))
						);
					}
				}

				if (error.status === 403) {
					this.router.navigate(['/']);
				}

				return throwError(error);
			})
		);
	}

	private attachHeaders(request: HttpRequest<object>): HttpRequest<object> {
		if (request.url === this.authFacade.getLoginPath()) {
			if (this.oAuthDisabled) {
				return request;
			}

			return request.clone({
				setHeaders: {
					Authorization: `Basic ${this.authFacade.getOAuthToken()}`,
				},
			});
		}

		return this.addAuthenticationToken(request);
	}

	private addAuthenticationToken(request: HttpRequest<object>): HttpRequest<object> {
		const accessToken = this.authFacade.getAccessToken();

		if (!accessToken) {
			return request;
		}

		return request.clone({
			setHeaders: {
				authorization: `Bearer ${accessToken}`,
			},
		});
	}
}
